import { useUserStore } from '~/store/user'

export type SocialProvider = 'google' | 'microsoft' | 'facebook'

/** Query params the API callback hands back (code / register / selection / error). */
export type SocialCallbackParams = Record<string, string>

/** Result message the popup posts to the window that opened it. */
interface SocialResultMessage {
  source: typeof MESSAGE_SOURCE
  type: 'result'
  params: SocialCallbackParams
}

/** Receipt the opener posts back so the popup knows it may close. */
interface SocialAckMessage {
  source: typeof MESSAGE_SOURCE
  type: 'ack'
}

export const MESSAGE_SOURCE = 'mw-social-login'
export const POPUP_CHANNEL = 'mw-social-login'
export const POPUP_WINDOW_NAME = 'mwSocialLogin'

const POPUP_WIDTH = 520
const POPUP_HEIGHT = 680

export function isSocialResultMessage(data: unknown): data is SocialResultMessage {
  const message = data as SocialResultMessage | null
  return (
    !!message
    && message.source === MESSAGE_SOURCE
    && message.type === 'result'
    && typeof message.params === 'object'
    && message.params !== null
  )
}

export function isSocialAckMessage(data: unknown): data is SocialAckMessage {
  const message = data as SocialAckMessage | null
  return !!message && message.source === MESSAGE_SOURCE && message.type === 'ack'
}

/** Centre the popup on the screen the console window is actually on. */
function popupFeatures(): string {
  const dualLeft = window.screenLeft ?? window.screenX
  const dualTop = window.screenTop ?? window.screenY
  const outerWidth = window.outerWidth || document.documentElement.clientWidth
  const outerHeight = window.outerHeight || document.documentElement.clientHeight
  const left = Math.max(0, dualLeft + (outerWidth - POPUP_WIDTH) / 2)
  const top = Math.max(0, dualTop + (outerHeight - POPUP_HEIGHT) / 2)

  return [
    'popup=yes',
    `width=${POPUP_WIDTH}`,
    `height=${POPUP_HEIGHT}`,
    `left=${Math.round(left)}`,
    `top=${Math.round(top)}`,
    'scrollbars=yes',
    'resizable=yes',
  ].join(',')
}

// ── Flow state, module-scoped on purpose ────────────────────────────────────
// The social buttons live inside the first wizard step of the login/register
// pages, so the component that starts a popup can unmount while the popup is
// still open (user types behind it and advances the step). The relay listeners
// must outlive that component or the popup's result is silently lost — so the
// whole in-flight flow (listeners, popup handle, pending flag) lives here at
// module scope, between start() and either a result or the relay deadline.

const pending = ref<SocialProvider | null>(null)

export function setNativeSocialPending(provider: SocialProvider | null) { pending.value = provider }

export function deliverSocialCallback(params: SocialCallbackParams): boolean {
  if (!inPlaceHandler) return false
  inPlaceHandler(params)
  return true
}

/**
 * How long the relay stays armed. The provider's OAuth state expires server
 * side after 10 minutes, so a result arriving later cannot be honoured anyway
 * — this is the only thing that ends a flow the user walked away from.
 */
const RELAY_DEADLINE_MS = 10 * 60 * 1000

let popup: Window | null = null
let channel: BroadcastChannel | null = null
let dismissPoll: ReturnType<typeof setInterval> | null = null
let deadline: ReturnType<typeof setTimeout> | null = null
let settled = true
let navigateToCallback: ((params: SocialCallbackParams) => void) | null = null

/**
 * A page that can finish the round trip WITHOUT navigating.
 *
 * Deliberately outside the per-flow state `teardown()` clears, and set through
 * the exported setter below rather than by assigning `navigateToCallback`:
 * that one is assigned inside `start()` and nulled on teardown, so anything a
 * page put there would be wiped by the next click.
 *
 * The checkout registers one because the desktop popup path used to navigate
 * the MAIN window to `/auth/social/callback` and back — two route changes,
 * which tore the checkout down and rebuilt it, scrolled to the top twice, and
 * wiped the one field the draft never keeps. Nothing else needs it; every other
 * page keeps the navigation, which is why this is a fallback and not a
 * replacement.
 */
let inPlaceHandler: ((params: SocialCallbackParams) => void) | null = null

/**
 * Register (or clear) the in-place handler.
 *
 * Module-scoped for the same reason the relay is: the component that opens a
 * popup can unmount while it is still open. The obligation that comes with
 * that is the caller's — clear it on unmount, or a popup started later from
 * `/login` is handed to a checkout that is no longer on screen.
 */
export function setSocialCallbackHandler(
  fn: ((params: SocialCallbackParams) => void) | null,
) {
  inPlaceHandler = fn
}

function stopDismissPoll() {
  if (dismissPoll) {
    clearInterval(dismissPoll)
    dismissPoll = null
  }
}

function teardown() {
  stopDismissPoll()
  if (deadline) {
    clearTimeout(deadline)
    deadline = null
  }
  window.removeEventListener('message', onWindowMessage)
  channel?.close()
  channel = null
  popup = null
  navigateToCallback = null
}

/** End an in-flight flow without a result: the deadline, or a fresh start(). */
function abandon() {
  if (settled) return
  settled = true
  pending.value = null
  teardown()
}

/**
 * Confirm receipt to the popup on both transports: directly (when it still has
 * an opener reference) and on the broadcast channel (when COOP severed it).
 */
function acknowledge(event?: MessageEvent) {
  const ack: SocialAckMessage = { source: MESSAGE_SOURCE, type: 'ack' }
  try {
    ;(event?.source as Window | null)?.postMessage(ack, window.location.origin)
  }
  catch {
    // Popup already gone; the channel below still covers it.
  }
  if (typeof BroadcastChannel !== 'undefined') {
    const ackChannel = channel ?? new BroadcastChannel(POPUP_CHANNEL)
    ackChannel.postMessage(ack)
    if (ackChannel !== channel) ackChannel.close()
  }
}

function settle(params: SocialCallbackParams, event?: MessageEvent) {
  if (settled) return
  settled = true
  pending.value = null

  // Captured before teardown(), which clears it.
  const navigate = navigateToCallback

  // Ack BEFORE teardown so the popup's close-wait resolves, then close it
  // ourselves as belt and braces. Once the provider swapped the browsing
  // context group this handle is inert, which is why the popup closes itself.
  acknowledge(event)
  try {
    popup?.close()
  }
  catch {
    // Cross-origin popup already gone; nothing to close.
  }
  teardown()

  // The callback route owns every outcome (handoff exchange, the address
  // step, multi-account selection, error display) — reuse it verbatim.
  const { display: _display, ...query } = params
  navigate?.(query)
}

function onWindowMessage(event: MessageEvent) {
  if (event.origin !== window.location.origin) return
  if (!isSocialResultMessage(event.data)) return
  settle(event.data.params, event)
}

function onChannelMessage(event: MessageEvent) {
  if (!isSocialResultMessage(event.data)) return
  settle(event.data.params)
}

/**
 * Social login: native browser + proof-bound callback on Capacitor;
 * popup-first on desktop and full-page-redirect on mobile browsers —
 * the pattern Google Identity Services, MSAL and the Facebook JS SDK all use.
 * A popup keeps the console page (and any checkout state on it) alive; mobile
 * browsers handle popups poorly, so they keep the same-tab redirect.
 *
 * The popup runs the whole OAuth dance, lands back on `/auth/social/callback`
 * with `display=popup`, posts its query params to this window and waits for
 * our ack before closing (no ack → it runs the flow itself instead of dying
 * silently). We then hand those params to the callback route in the main
 * window, which owns the code exchange / registration / account-selection UI.
 */
export function useSocialLogin() {
  const userStore = useUserStore()
  const localePath = useLocalePath()
  const { isMobile } = useCapacitorPlatform()
  const nuxtApp = useNuxtApp()
  const toast = useToast()
  const { t } = useI18n()

  /**
   * Desktop only: a real pointer and enough room for a 520px window.
   * Touch/narrow browsers use redirects; Capacitor has its own native transport.
   */
  function shouldUsePopup(): boolean {
    if (import.meta.server || isMobile.value) return false
    return window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)').matches
  }

  function start(provider: SocialProvider, redirect?: string) {
    // Never swallow the click because a previous flow is still armed: the
    // popup handle goes inert the moment the provider swaps the browsing
    // context group, so "a flow is pending" can just as easily mean "the user
    // closed the popup ten seconds ago". Drop the old flow and open a new one.
    abandon()

    if (isMobile.value) {
      const native = nuxtApp.$nativeSocialLogin
      if (!native) {
        toast.add({ title: t('checkout.socialError.generic'), color: 'error' })
        return
      }
      void native.start(provider, userStore.buildSocialLoginUrl(provider, redirect), redirect).catch(() => {
        pending.value = null
        toast.add({ title: t('checkout.socialError.generic'), color: 'error' })
      })
      return
    }

    if (!shouldUsePopup()) {
      pending.value = provider
      userStore.startSocialLogin(provider, redirect)
      return
    }

    // Popup-blocker rule #1: open synchronously inside the user gesture, with
    // the final URL already resolved. Nothing may be awaited before this line.
    const popupUrl = userStore.buildSocialLoginUrl(provider, redirect, 'popup')
    const opened = window.open(popupUrl, POPUP_WINDOW_NAME, popupFeatures())

    // Popup-blocker rule #2: if it was blocked, degrade to the redirect flow
    // instead of leaving the user staring at a dead button.
    if (!opened || opened.closed || typeof opened.closed === 'undefined') {
      pending.value = provider
      userStore.startSocialLogin(provider, redirect)
      return
    }

    settled = false
    popup = opened
    pending.value = provider
    navigateToCallback =
      inPlaceHandler ??
      ((query) => {
        void navigateTo(localePath({ path: '/auth/social/callback', query }))
      })

    window.addEventListener('message', onWindowMessage)
    // Fallback channel: providers with COOP headers (Google) sever
    // `window.opener`, so in practice this is often the transport that
    // actually delivers (same-origin, and we only listen while a login of
    // ours is actually pending).
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(POPUP_CHANNEL)
      channel.onmessage = onChannelMessage
    }

    opened.focus?.()

    // `popup.closed` IS NOT a dismissal signal here, and treating it as one is
    // what broke this flow: Google and Entra serve their consent screens with
    // `Cross-Origin-Opener-Policy: same-origin`, which swaps the browsing
    // context group, and from that moment (~250ms after the window opens) the
    // opener's handle reports `closed === true` for a window that is alive and
    // showing the account chooser. Tearing the relay down on it left the
    // console deaf by the time the result arrived, so the popup got no ack and
    // — by design — completed the login inside itself, stranding the user on
    // the sign-in page behind it.
    //
    // So the poll may only ever clear the BUTTON state, never the listeners,
    // and it waits for the opener to have focus back as well: while the popup
    // is genuinely open it holds focus, so `closed && document.hasFocus()` is
    // the closest thing to "the user dismissed it" that survives the swap. The
    // worst case is a spinner that stops early on a user who alt-tabbed back —
    // the flow behind it still completes.
    dismissPoll = setInterval(() => {
      if (settled) {
        stopDismissPoll()
        return
      }
      if (!popup?.closed || !document.hasFocus()) return
      pending.value = null
      stopDismissPoll()
    }, 400)

    // The only thing that ends a flow nobody came back to.
    deadline = setTimeout(abandon, RELAY_DEADLINE_MS)
  }

  return { pending, start, shouldUsePopup }
}
